import { Group, Portal, Stack, Text, useMantineTheme } from '@mantine/core';
import { ResponsiveLine, type SliceTooltipProps } from '@nivo/line';
// @ts-expect-error
import { nice, ticks } from 'd3-array';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { upperFirst } from 'lodash-es';
import { LegendSwatch } from 'modules/Analytics/components/charts/LegendSwatch';
import { uniqueBy } from 'remeda';
import { BarChartTooltip } from 'shared/charts';

type CompanyLine = {
  company: string;
  name: string;
  color: string;
  months: Record<string, number>;
};

type CompanySeries = { id: string; color: string; data: Array<{ x: Date; y: number }> };

type CompanyLinesChartProps = {
  lines: ReadonlyArray<CompanyLine>;
  formatValue: (value: number) => string;
  legendTarget: string;
  yAxis?: { max: number; format: (value: number) => string };
};

export const CompanyLinesChart = ({
  lines,
  formatValue,
  legendTarget,
  yAxis,
}: CompanyLinesChartProps) => {
  const profile = useProfile();
  const theme = useMantineTheme();

  const highestValue = Math.max(0, ...lines.flatMap(({ months }) => Object.values(months)));
  const yAxisUpperBound = yAxis?.max ?? nice(0, highestValue + 10, 8)[1];
  const yValues = ticks(0, yAxisUpperBound, 8);

  const series = lines.map(
    ({ company, color, months }): CompanySeries => ({
      id: company,
      color,
      data: Object.entries(months).map(([monthEnd, value]) => ({
        x: new Date(dayjs(monthEnd).startOf('month').format('YYYY-MM-DD')),
        y: value,
      })),
    }),
  );
  const monthStarts = uniqueBy(
    series.flatMap(({ data }) => data.map(({ x }) => x)),
    monthStart => monthStart.getTime(),
  ).toSorted((earlier, later) => earlier.getTime() - later.getTime());

  const sliceTooltip = ({ slice }: SliceTooltipProps<CompanySeries>) => (
    <BarChartTooltip
      id="tooltip-fix"
      style={{
        backgroundColor: theme.white,
        color: theme.colors.dark[0],
        border: `1px solid ${theme.colors.gray[2]}`,
        borderRadius: 12,
        padding: 'var(--mantine-spacing-space3)',
        boxShadow: '0 12px 28px rgba(32, 10, 66, 0.14)',
      }}
    >
      <Stack gap="space3">
        <Text fw={700}>
          {upperFirst(
            slice.points[0]?.data.x.toLocaleDateString(profile.locale, {
              month: 'long',
              year: 'numeric',
            }),
          )}
        </Text>
        <Stack component="ul" gap="space2" style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
          {lines.map(({ company, name, color }) => {
            const point = slice.points.find(candidate => candidate.seriesId === company);
            if (point === undefined) return null;

            return (
              <Group key={company} component="li" gap="space3" wrap="nowrap">
                <LegendSwatch color={color} />
                <Text style={{ whiteSpace: 'nowrap' }}>{name}</Text>
                <Text fw={700} ml="auto" style={{ whiteSpace: 'nowrap' }}>
                  {formatValue(point.data.y)}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </BarChartTooltip>
  );

  return (
    <>
      <ResponsiveLine
        isInteractive
        enableSlices="x"
        useMesh
        curve="monotoneX"
        enableCrosshair
        margin={{ top: 10, right: 15, bottom: 35, left: 55 }}
        axisBottom={{
          tickPadding: 15,
          tickSize: 0,
          tickValues: monthStarts,
          format: (date: Date) => date.toLocaleDateString(profile.locale, { month: 'short' }),
        }}
        axisLeft={{ tickPadding: 15, tickSize: 0, tickValues: yValues, format: yAxis?.format }}
        gridYValues={yValues}
        sliceTooltip={sliceTooltip}
        yScale={{ type: 'linear', min: 0, max: yAxisUpperBound }}
        xScale={{ type: 'time' }}
        enableGridX={false}
        pointSize={8}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        colors={{ datum: 'color' }}
        theme={{
          grid: {
            line: { stroke: theme.colors.gray[3], strokeDasharray: 7, strokeDashoffset: 15 },
          },
          text: { fontSize: 12, fill: theme.colors.gray[6] },
        }}
        data={series}
      />
      <Portal target={legendTarget}>
        <Group
          component="ul"
          w="100%"
          gap="space10"
          justify="center"
          mt="space5"
          style={{ padding: 0, listStyleType: 'none' }}
        >
          {lines.map(({ company, name, color }) => (
            <Group key={company} component="li" gap="space3">
              <LegendSwatch color={color} />
              <Text size="xs">{name}</Text>
            </Group>
          ))}
        </Group>
      </Portal>
    </>
  );
};
